import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { axiosErr } from "@hilos/types/axios";
import { Channel } from "@hilos/types/private-schema";
import HeaderNavigation from "src/components/HeaderNavigation";
import useCleanTimeout from "src/hooks/useCleanTimeout";
import useHilosStore from "src/hooks/useHilosStore";
import { API_ROUTES, buildAPIRoute, buildRoute } from "src/router/router";
import TechProviderConnectingModal from "./TechProviderConnectingModal";
import TechProviderOnboardingInstructions from "./TechProviderOnboardingInstructions";

export default function TechProviderOnboarding() {
  const { t } = useTranslation();
  const { session, reloadSession } = useHilosStore();
  const [searchParams] = useSearchParams();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(searchParams.get("error") as string);
  const [channel, setChannel] = useState<Channel | undefined>(undefined);
  const [connectionStatus, setConnectionStatus] = useState<
    "CONNECTING" | "SUCCESS" | "ERROR" | "NOT_STARTED"
  >("NOT_STARTED");
  const timeout = useCleanTimeout();
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();

  const FB_CONFIG_ID = useMemo(() => {
    return session?.account.feature_flags.includes("use-hilos-bsp")
      ? process.env.REACT_APP_FB_LOGIN_CONFIG_ID
      : process.env.REACT_APP_AC_FB_LOGIN_CONFIG_ID;
  }, [session?.account.feature_flags]);

  const FB_APP_ID = useMemo(() => {
    return session?.account.feature_flags.includes("use-hilos-bsp")
      ? process.env.REACT_APP_FB_APP_ID
      : process.env.REACT_APP_AC_FB_APP_ID;
  }, [session?.account.feature_flags]);

  const sessionInfoListener = (event) => {
    if (event.origin !== "https://www.facebook.com") return;
    console.log(event);
  };

  const exchangeCodeForAccessToken = useCallback(
    async (code) => {
      setConnectionStatus("CONNECTING");
      setError("");
      setShowModal(true);

      try {
        const response = await axios.post(
          buildAPIRoute(API_ROUTES.CLOUD_API_EXCHANGE_CODE),
          {
            code,
          }
        );
        const channels: Channel[] = response.data;
        setConnectionStatus("SUCCESS");
        reloadSession();
        if (channels.length === 1) {
          const channel = channels[0];
          setChannel(channel);

          timeout.current = setTimeout(() => {
            navigate(
              buildRoute("config-channel-cloud-api-details", {
                id: channel.id,
              })
            );
          }, 1000 * 2);
        } else {
          timeout.current = setTimeout(() => {
            navigate(buildRoute("config-channel"));
          }, 1000 * 2);
        }
      } catch (err) {
        const errorAxios: axiosErr = err as axiosErr;
        if (errorAxios?.response?.status === 400) {
          setError(errorAxios?.response?.data as string);
        } else {
          setError(
            t(
              "channel:embedded-signup.generic-error",
              "Something went wrong and our team has been notified. Please try again later."
            )
          );
        }
        setConnectionStatus("ERROR");
      } finally {
        setSubmitting(false);
        setShowModal(true);
      }
    },
    [t, navigate, timeout, reloadSession]
  );

  const handleClose = () => {
    setShowModal(false);
    // window.location.reload();
  };

  const launchWhatsAppSignup = useCallback(() => {
    //@ts-ignore
    const FB = window.FB;
    setConnectionStatus("CONNECTING");
    setSubmitting(true);
    try {
      FB.login(
        function (response) {
          console.log(response);
          if (response.authResponse) {
            const code = response.authResponse.code;
            exchangeCodeForAccessToken(code);
          } else {
            const errorMsg = t(
              "channel:embedded-signup.flow-canceled",
              "Facebook login was cancelled or was not completed. Please try again!"
            );
            setError(errorMsg);
            searchParams.set("error", errorMsg);
            // window.location.reload();
          }
        },
        {
          config_id: FB_CONFIG_ID, // configuration ID goes here
          response_type: "code", // must be set to 'code' for System User access token
          override_default_response_type: true, // when true, any response types passed in the "response_type" will take precedence over the default types
          extras: {
            sessionInfoVersion: "3",
            setup: {
              business: {
                name: session?.account.name,
                email: session?.email,
                // timezone: "UTC-08:00",
              },
            },
            version: "v3",
          },
        }
      );
    } catch (error) {}
  }, [session, exchangeCodeForAccessToken, t, searchParams]);

  useEffect(() => {
    // @ts-ignore
    if (!window.fbInitialized) {
      // @ts-ignore
      window.fbAsyncInit = function () {
        // @ts-ignore
        window.FB.init({
          appId: FB_APP_ID,
          autoLogAppEvents: true,
          cookie: true,
          xfbml: true,
          version: "v22.0",
        });
      };

      const script = document.createElement("script");
      script.src = "https://connect.facebook.net/en_US/sdk.js";
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);
    }

    //@ts-ignore
    window.fbInitialized = true;
  }, [FB_APP_ID]);

  return (
    <div className="h-screen overflow-y-auto bg-gray-50 pb-4">
      {/* Header */}
      <div className="py-4 px-4 sm:border-b sm:border-gray-200 sm:px-6 lg:px-8">
        <HeaderNavigation
          navPages={[
            {
              name: "Home",
              url: buildRoute("dashboard"),
            },
            {
              name: "Settings",
              url: buildRoute("config-account"),
            },
            {
              name: "WhatsApp Accounts",
              url: buildRoute("config-channel"),
            },
            {
              name: t("settings:tech-provider.title", "Connecting your number"),
              url: buildRoute("config-channel-cloud-api-onboarding"),
            },
          ]}
        />
        <div className="lg:flex lg:items-center lg:justify-between">
          <div className="min-w-0 flex-1">
            <h2 className="mt-2 text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl">
              {t("settings:tech-provider.title", "Connecting your number")}
            </h2>
          </div>
        </div>
      </div>
      <TechProviderOnboardingInstructions
        launchWhatsAppSignup={launchWhatsAppSignup}
        submitting={submitting}
      />
      <TechProviderConnectingModal
        connectionStatus={connectionStatus}
        error={error}
        channel={channel}
        show={showModal}
        handleClose={handleClose}
      />
    </div>
  );
}
